/**
 * CR320: S1.2 Diagnostics — Overview Section
 * 
 * Status, engine, counters, control-plane state, watchdog.
 */

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useMemo, useState } from "react";
import { Clock, Copy, Shield, StopCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import type { S12RunData } from "@/hooks/admin/useS12DiagnosticsData";
import type { OcrFailedPageRow, TruthCounts } from "./S12DiagnosticsPanel";

interface S12OverviewSectionProps {
  runData: S12RunData;
  noProgressSeconds: number;
  truthCounts?: TruthCounts;
  isLogicallyComplete?: boolean;
  queueActiveOverride?: boolean;
  engineLabel?: string | null;
  failedPages?: OcrFailedPageRow[];
  lastAttemptNo?: number | null;
  lastAttemptStarted?: string | null;
  s1OcrAttemptId?: string | null;
  resolvedRunId?: string | null;
}

export function resolveS12ExecutionFlags(
  runStatus: string | null | undefined,
  isLogicallyComplete: boolean,
  queueActiveOverride: boolean,
): {
  normalizedStatus: string;
  hasStatusQueueMismatch: boolean;
  displayStatus: string;
  isActiveRun: boolean;
} {
  const normalizedStatus = String(runStatus ?? "").toLowerCase();
  const hasStatusQueueMismatch =
    !isLogicallyComplete &&
    queueActiveOverride &&
    normalizedStatus !== "running" &&
    normalizedStatus !== "in_progress";
  const displayStatus = isLogicallyComplete
    ? "COMPLETE"
    : hasStatusQueueMismatch
      ? "RUNNING (QUEUE_ACTIVE)"
      : String(runStatus ?? "UNKNOWN");
  const isActiveRun =
    !isLogicallyComplete &&
    ((normalizedStatus === "running" || normalizedStatus === "in_progress") || queueActiveOverride);
  return {
    normalizedStatus,
    hasStatusQueueMismatch,
    displayStatus,
    isActiveRun,
  };
}

export function S12OverviewSection({
  runData,
  noProgressSeconds,
  truthCounts,
  isLogicallyComplete = false,
  queueActiveOverride = false,
  engineLabel,
  failedPages = [],
  lastAttemptNo,
  lastAttemptStarted,
  s1OcrAttemptId,
  resolvedRunId,
}: S12OverviewSectionProps) {
  const [showFailedDetails, setShowFailedDetails] = useState(false);
  const watchdog = runData.watchdog_timeout_sec || 300;
  const {
    normalizedStatus,
    hasStatusQueueMismatch,
    displayStatus,
    isActiveRun,
  } = resolveS12ExecutionFlags(runData.status, isLogicallyComplete, queueActiveOverride);
  const isTerminalRun = isLogicallyComplete || [
    "complete",
    "complete_with_errors",
    "partial",
    "failed",
    "cancelled",
    "blocked_no_candidates",
  ].includes(normalizedStatus);
  const watchdogExceeded = noProgressSeconds >= watchdog;
  const watchdogPct = Math.min(100, Math.round((noProgressSeconds / watchdog) * 100));
  const totalPages = truthCounts?.renderReady ?? runData.pages_total ?? null;
  const terminalPages = truthCounts?.ocrComplete ?? runData.pages_ocr_completed ?? null;
  const pendingPages = totalPages != null && terminalPages != null ? Math.max(0, totalPages - terminalPages) : null;
  const failedCount = truthCounts?.ocrFailed ?? 0;

  const failedRowsCopyText = useMemo(() => {
    if (!failedPages.length) return "No failed OCR pages.";
    return failedPages
      .map((row) => {
        const code = row.ocr_error_code ?? "UNKNOWN";
        const message = (row.primary_ocr_error ?? "No error message").replace(/\s+/g, " ").trim();
        return `page=${row.page_number}\tcode=${code}\terror=${message}`;
      })
      .join("\n");
  }, [failedPages]);

  return (
    <div className="space-y-2 text-xs">
      {/* Run Context */}
      <div className="space-y-1">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Run Context</div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground">Status:</span>
            <Badge variant="outline" className="text-xs">{displayStatus}</Badge>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground">Engine:</span>
            <Badge variant="outline" className="text-xs font-mono">
              {engineLabel || runData.primary_ocr_engine || "—"}
            </Badge>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground">Pages detected:</span>
            <span className="font-mono">{totalPages ?? "—"}</span>
          </div>
        </div>
      </div>

      {/* Execution */}
      <div className="space-y-1">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Execution</div>
        {hasStatusQueueMismatch && (
          <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            Status mismatch detected: dashboard run row is terminal, but OCR queue still has pending/running pages.
          </div>
        )}
        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground">Progress:</span>
            <span className="font-mono">{terminalPages ?? "—"} / {totalPages ?? "—"}{pendingPages != null ? ` (${pendingPages} remaining)` : ""}</span>
          </div>
          <div className="flex items-center gap-1.5 justify-start sm:justify-end">
            <Clock className="h-3 w-3 text-muted-foreground" />
            {isActiveRun ? (
              <span className={`font-mono font-medium ${watchdogExceeded ? "text-destructive" : noProgressSeconds > watchdog * 0.5 ? "text-amber-600" : ""}`}>
                No progress: {formatDurationShort(noProgressSeconds)}
              </span>
            ) : isTerminalRun ? (
              <span className="font-mono text-muted-foreground">
                Completed {formatDistanceToNow(new Date(runData.completed_at || runData.last_progress_at), { addSuffix: true })}
              </span>
            ) : (
              <span className="font-mono text-muted-foreground">
                Last progress: {formatDurationShort(noProgressSeconds)} ago
              </span>
            )}
          </div>
        </div>
        {isActiveRun ? (
          <div className="space-y-0.5">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">
                Watchdog: {watchdog}s {watchdogExceeded && <Badge variant="destructive" className="text-[10px] ml-1">EXCEEDED</Badge>}
              </span>
            </div>
            <div className="h-1.5 w-full bg-muted/90 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  watchdogExceeded ? "bg-destructive" : watchdogPct > 50 ? "bg-amber-500" : "bg-emerald-500"
                }`}
                style={{ width: `${watchdogPct}%` }}
              />
            </div>
          </div>
        ) : (
          <div className="text-muted-foreground flex items-center gap-1.5">
            <Clock className="h-3 w-3" />
            <span>Run not active — watchdog idle.</span>
          </div>
        )}
      </div>

      {/* Utilities */}
      {(lastAttemptNo != null || lastAttemptStarted || s1OcrAttemptId || resolvedRunId) && (
        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Utilities</div>
          <div className="flex items-center gap-2 text-[10px] font-mono text-muted-foreground">
            {lastAttemptNo != null && <span>Attempt #{lastAttemptNo}</span>}
            {lastAttemptStarted && (
              <>
                <span className="text-muted-foreground/50">·</span>
                <span>Started {new Date(lastAttemptStarted).toLocaleString("en-CA", { timeZone: "UTC", hour12: false, month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })} UTC</span>
              </>
            )}
            {(s1OcrAttemptId || resolvedRunId) && (
              <>
                <span className="text-muted-foreground/50">·</span>
                <button
                  type="button"
                  className="hover:text-foreground transition-colors underline decoration-dotted"
                  title={`Attempt: ${s1OcrAttemptId ?? '—'}\nRun: ${resolvedRunId ?? '—'}`}
                  onClick={() => {
                    const text = `Attempt: ${s1OcrAttemptId ?? '—'}\nRun: ${resolvedRunId ?? '—'}`;
                    navigator.clipboard.writeText(text);
                  }}
                >
                  Copy IDs
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Outcomes */}
      <div className="space-y-1">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Outcomes</div>
        <div className="flex flex-wrap gap-1">
          {truthCounts ? (
            <>
              <CounterBadge label="Pages Detected" value={truthCounts.renderReady} />
              <CounterBadge label="OCR Terminal" value={truthCounts.ocrComplete} />
              <CounterBadge label="OCR Succeeded" value={truthCounts.ocrSucceeded} />
              <CounterBadge
                label="OCR Failed"
                value={truthCounts.ocrFailed}
                highlight
                clickable={failedCount > 0}
                onClick={failedCount > 0 ? () => setShowFailedDetails((prev) => !prev) : undefined}
              />
              <CounterBadge label="OCR Pending" value={Math.max(0, truthCounts.renderReady - truthCounts.ocrComplete)} />
              <CounterBadge label="OCR Running" value={truthCounts.ocrRunning} />
              <CounterBadge label="OCR Queued" value={truthCounts.ocrQueued} />
            </>
          ) : (
            <>
              <CounterBadge label="Rendered" value={runData.pages_rendered} />
              <CounterBadge label="OCR Terminal" value={runData.pages_ocr_completed} />
            </>
          )}
        </div>
      </div>
      {truthCounts && truthCounts.renderReady === 0 && truthCounts.ocrComplete === 0 && (
        <p className="text-[10px] text-muted-foreground italic">No truth rows yet — waiting for OCR page processing.</p>
      )}
      {showFailedDetails && failedCount > 0 && (
        <div className="rounded-md border bg-muted/20 p-2 space-y-1.5">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-medium">Failed OCR Pages ({failedPages.length})</div>
            <Button
              size="sm"
              variant="outline"
              className="h-6 text-[10px]"
              onClick={() => navigator.clipboard.writeText(failedRowsCopyText)}
            >
              <Copy className="h-3 w-3 mr-1" />
              Copy Failed Rows
            </Button>
          </div>
          <div className="max-h-52 overflow-auto rounded border bg-background">
            <table className="w-full text-[10px]">
              <thead className="sticky top-0 bg-muted/60">
                <tr className="text-left">
                  <th className="px-2 py-1 font-medium">Page</th>
                  <th className="px-2 py-1 font-medium">Code</th>
                  <th className="px-2 py-1 font-medium">Error</th>
                </tr>
              </thead>
              <tbody>
                {failedPages.map((row) => (
                  <tr key={row.page_number} className="border-t">
                    <td className="px-2 py-1 font-mono">{row.page_number}</td>
                    <td className="px-2 py-1 font-mono">{row.ocr_error_code ?? "UNKNOWN"}</td>
                    <td className="px-2 py-1">{row.primary_ocr_error ?? "No error message"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Control plane */}
      {runData.force_stop_requested && (
        <div className="flex flex-wrap gap-1.5 p-2 bg-muted/50 rounded-md border">
          <Shield className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
          <div className="space-y-0.5">
            {runData.force_stop_requested && (
              <div className="flex items-center gap-1.5 text-destructive">
                <StopCircle className="h-3 w-3" />
                <span>Stop requested</span>
                {runData.stop_requested_at && (
                  <span className="text-muted-foreground">
                    {formatDistanceToNow(new Date(runData.stop_requested_at), { addSuffix: true })}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Failure info */}
      {runData.failure_code && (
        <div className="p-2 bg-destructive/10 border border-destructive/20 rounded-md">
          <div className="flex items-center gap-1.5 text-destructive font-medium">
            <span>Failure:</span>
            <code className="font-mono">{runData.failure_code}</code>
          </div>
          {runData.failure_reason && (
            <p className="text-muted-foreground mt-0.5 line-clamp-2">{runData.failure_reason}</p>
          )}
        </div>
      )}
    </div>
  );
}

function CounterBadge({
  label,
  value,
  highlight,
  clickable,
  onClick,
}: {
  label: string;
  value: number | null;
  highlight?: boolean;
  clickable?: boolean;
  onClick?: () => void;
}) {
  return (
    <Badge
      variant={highlight && (value ?? 0) > 0 ? "default" : "outline"}
      className={`text-[10px] gap-1 ${clickable ? "cursor-pointer hover:opacity-85" : ""}`}
      onClick={onClick}
    >
      <span className="text-muted-foreground font-normal">{label}:</span>
      <span className="font-mono">{value ?? 0}</span>
    </Badge>
  );
}

function formatDurationShort(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}
